import { useCallback, useState } from 'react'
import type { PrerequisiteGate } from '@assessments/shared'
import { RichText } from '@/components/ui/RichText'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DownloadIcon } from '@/components/ui/icons'
import { useSession } from '../auth/SessionContext.js'
import { canEditAssessment } from '../auth/permissions.js'
import { useAssessmentOverview } from './useAssessmentOverview.js'
import { AssessmentSidebar } from './AssessmentSidebar.js'
import { OverviewActionBar } from './OverviewActionBar.js'
import { SubmitGate } from './SubmitGate.js'
import { SubmitConfirmDialog } from './SubmitConfirmDialog.js'
import { SubmissionConfirmation } from './SubmissionConfirmation.js'
import { useSubmitAssessment } from './useSubmitAssessment.js'
import type { SubmissionErrorCode } from '@assessments/shared'
import { SectionBucketList } from './SectionBucketList.js'
import { SummaryCards } from './SummaryCards.js'
import { WelcomePage } from './WelcomePage.js'
import { AcceptTerms } from '../accept-terms/AcceptTerms.js'

export interface AssessmentOverviewProps {
  organizationSurveyId: string
  onOpenSection?: (sectionId: string) => void
  /**
   * True once the caller has already shown this assessment's welcome this session, so a
   * return visit (e.g. Done editing, back-to-overview) lands on the section list instead
   * of the welcome again. Defaults to false for standalone entry.
   */
  welcomeDismissed?: boolean
  onWelcomeDismissed?: () => void
  /** Navigate to the Terms of Use accept screen. */
  onOpenTerms?: () => void
  onOpenGuidance?: () => void
  /** Back to the assessment list, from the sidebar. */
  onBackToList?: () => void
  onExportProgress?: () => void
  onManageCollaborators?: () => void
  /** Autosave status for the action bar, e.g. "Saved just now". */
  savedLabel?: string | null
}

export function AssessmentOverview({
  organizationSurveyId,
  onOpenSection,
  welcomeDismissed: welcomeDismissedProp = false,
  onWelcomeDismissed,
  onOpenTerms,
  onOpenGuidance,
  onBackToList,
  onExportProgress,
  onManageCollaborators,
  savedLabel,
}: AssessmentOverviewProps) {
  const { data, loading, error, reload } = useAssessmentOverview(organizationSurveyId)
  const { session, onSessionExpired } = useSession()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const submission = useSubmitAssessment(organizationSurveyId, {
    onSettled: reload,
    onSessionExpired,
  })
  const submitting = submission.status === 'submitting'
  const closeConfirm = useCallback(() => setConfirmOpen(false), [])
  const confirmSubmit = async () => {
    await submission.submit()
    setConfirmOpen(false)
  }

  // The authored welcome is shown once at entry; dismissing it reveals the overview.
  const [welcomeDismissed, setWelcomeDismissed] = useState(welcomeDismissedProp)

  // Company Administrator (org admin) and Administrator (Ethisphere admin) get
  // edit/open affordances; Org View Only (`User`) renders read-only.
  const canEdit = canEditAssessment(session?.user.roles)

  // The fresh result right after submitting, else what the overview reports on later visits.
  const submittedAt =
    submission.result?.submittedAt ??
    (data?.submitGate.submitted ? data.submitGate.submittedAt : null)

  if (loading) {
    return (
      <main className="min-w-0 px-6 py-10 text-[15px] text-subtle lg:px-10">Loading overview…</main>
    )
  }
  if (error || !data) {
    return (
      <main className="min-w-0 px-6 py-10 lg:px-10">
        <Card className="p-6 text-center text-[15px] text-subtle">
          Couldn’t load the assessment overview. Please try again.
        </Card>
      </main>
    )
  }

  if (data.welcome && !welcomeDismissed) {
    return (
      <WelcomePage
        welcome={data.welcome}
        onProceed={() => {
          setWelcomeDismissed(true)
          onWelcomeDismissed?.()
        }}
      />
    )
  }

  // After the welcome, an unaccepted T&C gate is a hard interstitial: the overview is
  // withheld until consent is recorded. Accepting reloads the overview so the
  // now-complete gate falls through to the sections.
  const termsGate = data.prerequisites?.find((p) => p.kind === 'terms_and_conditions')
  if (termsGate && !termsGate.complete) {
    return <AcceptTerms organizationSurveyId={organizationSurveyId} onAccepted={reload} />
  }

  // One handler for both the sidebar and the section list: a section-backed gate (the
  // Organizational Profile) opens as an ordinary section, any other kind routes to its
  // own screen.
  const openPrerequisite = (gate: PrerequisiteGate) => {
    if (gate.sectionId !== null) onOpenSection?.(gate.sectionId)
    else if (gate.kind === 'terms_and_conditions') onOpenTerms?.()
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-surface-page">
      <AssessmentSidebar
        overview={data}
        onOpenSection={onOpenSection}
        onBackToList={onBackToList}
        onOpenPrerequisite={openPrerequisite}
        onOpenGuidance={onOpenGuidance}
      />

      <div className="min-w-0 flex-1">
        <OverviewActionBar
          title="Overview"
          savedLabel={savedLabel}
          canEdit={canEdit}
          canSubmit={data.submitGate.canSubmit && !data.submitGate.submitted}
          submitEnabled={data.submitGate.ready}
          submitting={submitting}
          onSubmit={() => setConfirmOpen(true)}
          onManageCollaborators={onManageCollaborators}
        />
        <SubmitConfirmDialog
          open={confirmOpen}
          submitting={submitting}
          onConfirm={() => void confirmSubmit()}
          onCancel={closeConfirm}
        />

        <main className="min-w-0 px-6 py-10 lg:px-10">
          <div className="mx-auto max-w-[1180px]">
            <p className="mb-3 text-[13px] font-semibold tracking-[0.14em] text-success uppercase">
              Assessment overview
            </p>
            <h1 className="font-jakarta text-[40px] leading-[1.1] font-bold tracking-tight text-heading">
              {data.assessmentTitle}
            </h1>

            {/* The authored instructions live on the Guidance page; the overview carries the intro. */}
            <RichText
              html={data.welcome?.body ?? null}
              className="mt-4 max-w-[62ch] text-[19px] leading-relaxed text-body"
            />

            <div className="mt-6">
              {submission.status === 'error' && (
                <div
                  role="alert"
                  className="mb-8 rounded-lg border border-danger bg-danger/10 p-4 text-[15px] text-body"
                >
                  {submissionErrorMessage(submission.errorCode)}
                </div>
              )}
              {submittedAt && (
                <SubmissionConfirmation
                  submittedAt={submittedAt}
                  submittedByName={submission.result?.submittedBy.name}
                />
              )}
              {data.submitGate.canSubmit && <SubmitGate submitGate={data.submitGate} />}
            </div>

            {onExportProgress && (
              <Button
                onClick={onExportProgress}
                className="mt-7 h-auto gap-2.5 rounded-card px-5 py-3 text-[15px] font-semibold shadow-none hover:bg-primary-hover"
              >
                <DownloadIcon />
                Export progress
              </Button>
            )}

            <div className="mt-9">
              <SummaryCards
                summary={data.summary}
                overview={data}
                outstandingPrerequisites={data.prerequisites
                  .filter((p) => !p.complete)
                  .map((p) => p.title)}
              />
            </div>

            <SectionBucketList
              overview={data}
              canEdit={canEdit}
              onOpenSection={onOpenSection}
              onOpenPrerequisite={openPrerequisite}
            />
          </div>
        </main>
      </div>
    </div>
  )
}

function submissionErrorMessage(code: SubmissionErrorCode | null): string {
  switch (code) {
    case 'ALREADY_SUBMITTED':
    case 'ASSESSMENT_SUBMITTED':
      return 'This assessment has already been submitted.'
    case 'GATE_NOT_MET':
      return 'Something changed since this page loaded, so the assessment isn’t ready to submit yet. See what’s left below.'
    case 'SUBMISSION_WINDOW_CLOSED':
      return 'The submission window for this assessment has closed.'
    case 'NOT_APPLICATION_OWNER':
      return 'Only the assessment’s Application Owner can submit it.'
    case 'NOT_SUBMITTABLE':
      return 'This assessment can’t be submitted here.'
    default:
      return 'Couldn’t submit the assessment. Please try again.'
  }
}
